import { Injectable, computed, inject, signal } from '@angular/core';
import { StorageService } from './storage.service';

export type ThemeMode = 'dark' | 'light' | 'system';

const STORAGE_KEY = 'theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storage = inject(StorageService);

  private _prefersDark = signal(this.checkPrefersDark());

  readonly mode = signal<ThemeMode>(
    (this.storage.get(STORAGE_KEY) as ThemeMode) || 'system'
  );

  readonly isDark = computed(() => {
    const m = this.mode();
    return m === 'dark' || (m === 'system' && this._prefersDark());
  });

  constructor() {
    this.applyTheme(this.isDark());

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', (e) => {
      this._prefersDark.set(e.matches);
    });
  }

  toggle(): void {
    const next: Record<ThemeMode, ThemeMode> = {
      dark: 'light',
      light: 'system',
      system: 'dark',
    };
    const nextMode = next[this.mode()];
    this.setMode(nextMode);
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
    this.storage.set(STORAGE_KEY, mode);
    this.applyTheme(this.isDark());
  }

  private checkPrefersDark(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private applyTheme(dark: boolean): void {
    if (dark) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }
}
